import type {TaskDefinitionRequest} from 'src/api/ProactApiClient';
import {TaskDefinitionStateEnum} from 'src/api/ProactApiClient';
import {TASK_ASSIGNMENT_OPEN_STATES} from 'src/app/tasks/taskHelpers';
import type {OutboxExecutor} from 'src/offline/executorRegistry';
import {rewriteUploadPlaceholders} from 'src/offline/pendingUploads';
import {emptySplitApi} from 'src/redux/api';
import {store} from 'src/redux/store';
import {
  createDefinition,
  getAssignment,
  getDefinition,
  transitionAssignment,
  transitionDefinition,
} from './api';
import type {TransitionTaskAssignmentArgs} from './mutations';
import type {OfflineActionCreateRequest} from './queuedTransition';
import {stripQueuedTransition} from './queuedTransition';

// A read failure counts as "still open" so the original transition error is the
// one reported and classified.
const isStillOpen = async (assignmentId: number): Promise<boolean> => {
  try {
    return TASK_ASSIGNMENT_OPEN_STATES.includes(
      (await getAssignment(assignmentId)).state
    );
  } catch {
    return true;
  }
};

export const transitionTaskExecutor: OutboxExecutor = async payload => {
  const args = payload as TransitionTaskAssignmentArgs;
  try {
    await transitionAssignment(args.assignmentId, {
      transition: args.transition,
      note: args.note,
      occurredAt: new Date(args.occurredAt),
    });
  } catch (error) {
    // A replayed transition may already have landed; only report the error
    // while the assignment is still open.
    if (await isStillOpen(args.assignmentId)) throw error;
  }

  store.dispatch(emptySplitApi.util.invalidateTags(['TaskAssignments']));
  return {summary: `Assignment #${args.assignmentId} ${args.transition}`};
};

const isStillDraft = async (taskId: number): Promise<boolean> => {
  try {
    return (await getDefinition(taskId)).state === TaskDefinitionStateEnum.Draft;
  } catch {
    return true;
  }
};

export const createActionExecutor: OutboxExecutor = async (
  payload,
  clientSubmissionId,
  ctx
) => {
  const source = payload as OfflineActionCreateRequest;
  const request: TaskDefinitionRequest = {
    ...stripQueuedTransition(source),
    clientSubmissionId,
    inlineAttachments:
      typeof source.inlineAttachments === 'string'
        ? rewriteUploadPlaceholders(source.inlineAttachments, ctx)
        : source.inlineAttachments,
  };
  const response = await createDefinition(request);

  const queued = source.queuedTransition;
  if (queued) {
    try {
      await transitionDefinition(response.id, {
        transition: queued.name,
        note: '',
      });
    } catch (error) {
      // A replayed publish is rejected once the definition left draft; that is
      // the replay succeeding, not a failure to report.
      if (await isStillDraft(response.id)) throw error;
    }
  }

  store.dispatch(
    emptySplitApi.util.invalidateTags(['TaskDefinitions', 'TaskAssignments'])
  );
  return {
    summary: queued
      ? `Action #${response.id} created and ${queued.name}`
      : `Action #${response.id} created`,
  };
};
